import { useUiStore } from "src/components/main/uiStore";
import {
    Link,
    Outlet,
    useLoaderData,
    useLocation,
    useRevalidator,
    useSearchParams,
} from "react-router-dom";

import ServerFooter from "../common/ServerFooter";
import { useAuthStore, User } from "../main/authStore";
import { myAxios, pipelineOptions, projectCategoryColorCode } from "../common/functions";
import star from "src/img/star.png";
import unstar from "src/img/unstar.png";
import { Project } from "./types";
import UserInfo from "../common/userInfo";

export const extraFields = ["page", "limit", "fav"];
export default function ProjectList() {
    const { projects }: { projects: Project[] } = useLoaderData();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { revalidate } = useRevalidator();
    const user = useAuthStore(state => state.user) as User;
    const setUserInfo = useAuthStore(state => state.setUserInfo);
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const favoriteIds = user.fav?.projects ?? [];
    const onFilter = [...searchParams.keys()].some(key => !extraFields.includes(key));
    const showFav = searchParams.get("fav") === "1";

    const setParamToggle = (key: string, enabled: boolean) => {
        const params = new URLSearchParams(searchParams);
        if (enabled) params.set(key, "1");
        else params.delete(key);
        params.set("page", "1");
        setSearchParams(params, { replace: false });
    };


    const toggleFavorite = async (project: Project) => {
        const formData = new FormData();
        formData.append("id", project.id);
        const res = await myAxios({
            fileName: "/projects/toggleFav.php",
            formData,
        });
        if(!res) return;
        setUserInfo({ fav: res.data ?? [] });
        revalidate();
    };

    const goToNextStage = async (project: Project) => {
        const stages = pipelineOptions[project.pipeline as keyof typeof pipelineOptions] ?? [];
        const currentIndex = stages.indexOf(project.stage);
        if (currentIndex < 0 || currentIndex >= stages.length - 1) return;
        if (!window.confirm("Are you sure to set this project to the next stage?")) return;
        const formData = new FormData();
        formData.append("id", project.id);
        formData.append("stage", stages[currentIndex + 1]);
        setShowLoader(true);
        const res = await myAxios({ fileName: "projects/nextStage.php", formData });
        setShowLoader(false);
        if (!res) return;
        revalidate();
    };

    return (
        <div className={"cssContent" + (projects.length > 0 ? " pb8rem" : "")}>
            <nav className="mt-2 d-flex flex-wrap align-items-center gap-2">
                <button
                    type="button"
                    className={`btn ${showFav ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setParamToggle("fav", !showFav)}
                >
                    Favorites
                </button>
                <Link
                    className="btn btn-outline-primary position-relative"
                    to={{ pathname: "Filter", search: location.search }}
                    replace
                >
                    Filter
                    {onFilter ? (
                        <span
                            children="on"
                            className="badge text-bg-primary position-absolute top-0 start-100 translate-middle"
                            style={{ minWidth: "2.4em" }}
                        />
                    ) : null}
                </Link>
                <Link
                    className="btn btn-outline-primary"
                    to={{ pathname: "Export", search: location.search }}
                >
                    Export
                </Link>
                <Link className="btn btn-outline-primary ms-auto" to="Add">
                    Add
                </Link>
            </nav>
            {!projects.length ? (
                <div className="cssLoading">Empty</div>
            ) : (
                <div className="table-responsive">
                    <table className="table">
                        <thead className="noTableTopBorder">
                            <tr>
                                <th></th>
                                <th>Project Name</th>
                                <th>Project Manager</th>
                                <th>Pipeline</th>
                                <th>Stage</th>
                                <th>Prevailing Job</th>
                                <th>Certified Payroll</th>
                                <th>DIR Number</th>
                                <th>USA Expire</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map(project => {
                                const stages = pipelineOptions[project.pipeline as keyof typeof pipelineOptions] ?? [];
                                const stageIndex = stages.indexOf(project.stage);
                                const canAdvance = stageIndex > -1 && stageIndex < stages.length - 1;
                                return (
                                    <tr key={project.id}>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={() => toggleFavorite(project)}
                                            >
                                                <img
                                                    src={favoriteIds.includes(project.id) ? star : unstar}
                                                    alt=""
                                                />
                                            </button>
                                        </td>
                                        <td>{project.projectName}</td>
                                        <td>{project.projectManagerId && project.projectManagerName ? <UserInfo name = {project.projectManagerName} id = {project.projectManagerId}/> : null}</td>
                                        <td
                                            className="text-white fw-bold"
                                            style={{
                                                backgroundColor: project.pipeline
                                                    ? `rgb(${projectCategoryColorCode[project.pipeline as keyof typeof projectCategoryColorCode]?.rgb.join(",")})`
                                                    : undefined,
                                            }}
                                        >
                                            {project.subPipeline ? `${project.pipeline} - ${project.subPipeline}` : project.pipeline}
                                        </td>
                                        <td>
                                            <span>{project.stage}</span>
                                            {canAdvance ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm ms-2"
                                                    onClick={() => goToNextStage(project)}
                                                >
                                                    →
                                                </button>
                                            ) : null}
                                        </td>
                                        <td>{project.prevailing}</td>
                                        <td>{project.cpr}</td>
                                        <td>{project.dirNumber}</td>
                                        <td>{project.usaTicketExpirationDate}</td>
                                        <td>
                                            <Link className="btn btn-outline-primary" to={String(project.id)}>
                                                Detail
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {projects.length > 0 ? <ServerFooter length={projects.length} /> : null}
            <Outlet />
        </div>
    );
}
